import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, Archive, ArrowLeft, ClipboardList, MapPin, Package, Plus, ScanLine, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AjoutColisDialog } from "@/components/colis/AjoutColisDialog";
import { EcheanceBadge } from "@/components/colis/Echeance";
import { fmtDateHeure } from "@/components/scans/common";
import { api, errMsg } from "@/lib/api";
import { numeroLisible } from "@/lib/colisCode";
import { colisSubnav } from "@/lib/subnavs";
import { CreerListeDialog } from "./ListesColisPage";
import { InfoFichier, correspond } from "@/components/depot/InfoFichier";
import { ChariotVisuel } from "@/components/depot/ChariotVisuel";
import { EtagereVisuel } from "@/components/depot/EtagereVisuel";
import { useAuth } from "@/context/AuthContext";
import { peut } from "@/lib/permissions";
import { lireCode } from "@/lib/colisCode";
import { useDouchette } from "@/lib/douchette";
import { CapteurLecteur } from "@/components/colis/ChampLecteur";

export function StatutColis({ c, detail = false }) {
  const instance = c.statut === "en_instance" || c.statut === "rts";
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${c.statut === "rts"
      ? "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40"
      : instance ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"
        : "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30"}`} title={c.instance_en_cours || undefined}>
      {c.statut_label || c.statut}{detail && instance && c.instance_en_cours ? ` · ${c.instance_en_cours}` : ""}
    </span>
  );
}

export default function ColisPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [ajout, setAjout] = useState(false);
  const [codeLu, setCodeLu] = useState(null);
  // Lecteur intégré (Zebra) / douchette : un scan cherche le colis (où il est, sa fiche).
  const chercherScan = (code) => { const lu = lireCode(code); setQ(lu.type ? lu.numero : code); };
  useDouchette(chercherScan, !ajout);
  const [filtre, setFiltre] = useState("instance"); // en dessous : les colis en instance
  // « Colis antérieurs » : fiches sans nouvelle information depuis 10 jours.
  const [anciens, setAnciens] = useState(false);
  const basculerAnciens = () => { setAnciens((a) => !a); setFiltre((f) => (anciens ? "instance" : "")); };
  const [sel, setSel] = useState(null); // emplacement (chariot / place) choisi
  const voirDepot = peut(user, "colis_depot");
  const peutAjouter = peut(user, "colis_creer") || peut(user, "colis_instance");
  const voirCage = peut(user, "colis_cage");
  const [depot, setDepot] = useState(null);
  const [cage, setCage] = useState(null);
  const chargerLieux = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([
        voirDepot ? api.get("/depot/etat") : Promise.resolve({ data: null }),
        voirCage ? api.get("/depot/etat", { params: { zone: "cage" } }) : Promise.resolve({ data: null }),
      ]);
      setDepot(d.data); setCage(c.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [voirDepot, voirCage]);
  useEffect(() => { chargerLieux(); }, [chargerLieux]);

  // Tous les emplacements (chariots du dépôt + places de la cage) avec leurs colis.
  const lieux = useMemo(() => [
    ...(depot?.chariots || []).flatMap((ch) => ch.emplacements.map((e) => ({ ...e, chariot: ch, zone: "depot" }))),
    ...(cage?.chariots || []).flatMap((ch) => ch.emplacements.map((e) => ({ ...e, chariot: ch, zone: "cage" }))),
  ], [depot, cage]);
  const recherche = q.trim().length >= 3 ? q.trim() : "";
  const trouves = useMemo(() => (recherche
    ? lieux.flatMap((e) => e.colis.filter((c) => correspond(c, recherche)).map((c) => ({ ...c, emp: e })))
    : []), [lieux, recherche]);
  const surligne = [...new Set(trouves.map((t) => t.emp.id))];
  // Colis trouvé : son chariot / sa place s'ouvre tout de suite à droite.
  const premier = trouves[0]?.emp.id;
  useEffect(() => { if (premier) setSel(premier); }, [premier]);
  const selEmp = lieux.find((e) => e.id === sel);
  const chariotsPleins = (depot?.chariots || []).filter((ch) => ch.nb > 0);
  const [creerListe, setCreerListe] = useState(false);

  const charger = useCallback(async (texte, f, a) => {
    try {
      const params = {};
      if (texte?.trim()) params.q = texte;
      if (f) params.filtre = f;
      if (a && !texte?.trim()) params.anciens = true;
      const { data: d } = await api.get("/colis", { params });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => {
    // pendant une recherche : toutes les fiches qui correspondent (pas seulement en instance)
    const t = setTimeout(() => charger(q, q.trim() ? "" : filtre, anciens), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, filtre, anciens, charger]);

  const cpt = data?.compteurs || {};
  const FILTRES = [
    ["", "Tous", null, ""],
    ["instance", "En instance", cpt.instance, "amber"],
    ["rts", "RTS", cpt.rts, "red"],
    ["remise", "À remettre en trafic", cpt.remise, "sky"],
    ["limite_proche", "Date limite proche (≤ 2 j)", cpt.limite_proche, "orange"],
    ["limite_depassee", "Date limite dépassée", cpt.limite_depassee, "red"],
  ];
  const COUL = {
    amber: "border-amber-500/50 text-amber-600 dark:text-amber-400", sky: "border-sky-500/50 text-sky-600 dark:text-sky-400",
    orange: "border-orange-500/50 text-orange-600 dark:text-orange-400", red: "border-red-500/60 text-red-600 dark:text-red-400",
  };

  const liste = data?.colis || [];

  return (
    <AppShell title="Colis" subnav={colisSubnav()}>
      <CapteurLecteur onScan={chercherScan} actif={!ajout} />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Colis</h1>
          <p className="text-sm text-muted-foreground">{data ? `${data.total} colis enregistré(s)` : "Chargement…"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {peut(user, "colis_listes") && peut(user, "listes_creer") && <Button variant="outline" onClick={() => setCreerListe(true)} data-testid="btn-creer-liste"><ClipboardList size={15} /> Créer une liste</Button>}
          {peutAjouter && <Button variant="outline" onClick={() => setAjout("saisie")} data-testid="btn-ajouter-colis"><Plus size={15} /> Saisir un colis</Button>}
          {peutAjouter && <Button onClick={() => setAjout("scan")} data-testid="btn-scanner-colis"><ScanLine size={15} /> Scanner un colis</Button>}
        </div>
      </div>

      {/* Grande barre de recherche : n° de colis (ou scan), RS expéditeur / destinataire */}
      <div className="relative mb-5">
        <Search size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un colis : n°, expéditeur ou destinataire… (ou scannez-le)"
          className="pl-12 pr-12 h-14 text-lg rounded-xl" data-testid="colis-recherche" />
        {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground" aria-label="Effacer"><X size={18} /></button>}
      </div>

      {recherche && (
        <section className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="colis-ou">
          <h2 className="font-heading font-semibold mb-2 flex items-center gap-2"><MapPin size={16} /> Où est le colis ?</h2>
          {trouves.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sur aucun chariot du dépôt restant ni aucune étagère de la cage (inventaires du jour).</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {trouves.slice(0, 50).map((t) => (
                <li key={`${t.emp.zone}-${t.numero}`} className="py-2" data-testid={`ou-${t.numero}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-bold">{numeroLisible(t.numero)}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${t.emp.zone === "cage" ? "bg-violet-500/15 text-violet-600 dark:text-violet-400" : "bg-sky-500/15 text-sky-600 dark:text-sky-400"}`}>
                      {t.emp.zone === "cage" ? "Cage" : "Dépôt restant"}
                    </span>
                    <button className="font-semibold text-primary hover:underline" onClick={() => setSel(t.emp.id)}>{t.emp.nom}</button>
                    {t.fiche && <StatutColis c={t.fiche} detail />}
                    {t.fiche && peut(user, "colis_colis") && <Link to={`/colis/${t.fiche.id}`} className="text-xs text-primary hover:underline">Fiche</Link>}
                  </div>
                  <InfoFichier info={t.info} compact recherche={recherche} numero={t.numero} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {(voirDepot || voirCage) && (
        <div className="grid gap-5 lg:grid-cols-[1fr_340px] items-start mb-8">
          <div className="space-y-6 min-w-0">
            {voirDepot && (
              <section data-testid="colis-vue-depot">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <h2 className="font-heading font-semibold">Dépôt restant</h2>
                  <Link to="/colis/depot" className="text-xs text-primary hover:underline">Ouvrir</Link>
                </div>
                {chariotsPleins.length === 0 ? <p className="text-sm text-muted-foreground">Aucun colis sur les chariots (pas encore d'inventaire aujourd'hui).</p> : (
                  <div className="flex flex-wrap items-end gap-x-5 gap-y-3 text-foreground">
                    {chariotsPleins.map((ch) => (
                      <div key={ch.id} className="flex flex-col items-center gap-0.5">
                        <ChariotVisuel type={ch.type} lettre={ch.lettre} emplacements={ch.emplacements} selection={sel} surligne={surligne}
                          onSelect={(e) => setSel(e.id)} taille={0.7} testId={`vue-chariot-${ch.lettre}`} />
                        <span className="text-[11px] text-muted-foreground">{ch.nb} colis</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
            {voirCage && (
              <section data-testid="colis-vue-cage">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <h2 className="font-heading font-semibold">Colis cage</h2>
                  <Link to="/colis/cage" className="text-xs text-primary hover:underline">Ouvrir</Link>
                </div>
                {(cage?.chariots || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucune étagère configurée.</p> : (
                  <div className="flex flex-wrap gap-5">
                    {cage.chariots.map((et) => (
                      <EtagereVisuel key={et.id} lettre={et.lettre} lignes={et.lignes} colonnes={et.colonnes} emplacements={et.emplacements} compact
                        selection={sel} surligne={surligne} onSelect={(e) => setSel(e.id)} testId={`vue-etagere-${et.lettre}`} />
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
          <aside className="rounded-xl border border-border bg-card p-4 lg:sticky lg:top-4" data-testid="colis-vue-detail">
            {!selEmp ? <p className="text-sm text-muted-foreground">Touchez un chariot ou une place d'étagère pour voir ses colis.</p> : (
              <>
                <p className="font-heading font-semibold">{selEmp.nom}</p>
                <p className="text-xs text-muted-foreground mb-2">{selEmp.zone === "cage" ? "Cage" : "Dépôt restant"} · {selEmp.colis.length} colis</p>
                {selEmp.colis.length === 0 ? <p className="text-sm text-muted-foreground">Vide.</p> : (
                  <ul className="divide-y divide-border max-h-[50vh] overflow-y-auto">
                    {selEmp.colis.map((c) => (
                      <li key={c.numero} className={`py-2 text-sm ${correspond(c, recherche) ? "bg-red-500/10 -mx-2 px-2 rounded" : ""}`}>
                        <div className="flex items-center justify-between gap-2">
                          {c.fiche && peut(user, "colis_colis") ? <Link to={`/colis/${c.fiche.id}`} className="font-mono font-bold hover:underline">{numeroLisible(c.numero)}</Link>
                            : <span className="font-mono font-bold">{numeroLisible(c.numero)}</span>}
                          {c.fiche && <StatutColis c={c.fiche} />}
                        </div>
                        <InfoFichier info={c.info} compact numero={c.numero} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </aside>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center gap-2 mb-3">
        <h2 className="font-heading font-semibold flex items-center gap-2 shrink-0 mr-2" data-testid="colis-titre-liste">
          {anciens && !q.trim() ? <><Archive size={16} /> Colis antérieurs</> : <><AlertOctagon size={16} /> {recherche ? "Fiches colis trouvées" : "Colis en instance"}</>}
        </h2>
        {!q.trim() && <div className="flex flex-wrap gap-1.5" data-testid="colis-filtres">
          {FILTRES.map(([k, l, n, c]) => (
            <button key={k || "tous"} onClick={() => setFiltre(k)} data-testid={`filtre-${k || "tous"}`}
              className={`text-xs px-3 h-9 rounded-md border ${filtre === k ? "border-primary bg-primary/10 text-primary font-semibold" : n ? `${COUL[c]} hover:bg-accent` : "border-border hover:bg-accent"}`}>
              {l}{n ? ` (${n})` : ""}
            </button>
          ))}
        </div>}
        {!q.trim() && (anciens || peut(user, "colis_anterieurs")) && (
          <Button variant={anciens ? "default" : "outline"} size="sm" className="lg:ml-auto h-9" onClick={basculerAnciens} data-testid="btn-colis-anterieurs">
            {anciens ? <><ArrowLeft size={14} /> Colis récents</> : <><Archive size={14} /> Colis antérieurs{data?.anterieurs ? ` (${data.anterieurs})` : ""}</>}
          </Button>
        )}
      </div>
      {anciens && !q.trim() && (
        <p className="text-xs text-muted-foreground mb-3" data-testid="colis-anterieurs-info">
          Fiches sans nouvelle information depuis {data?.anterieur_jours || 10} jours. Une nouvelle instance, date ou photo les remet dans les colis récents.
          Sans nouvelle information pendant 3 mois, la fiche est supprimée définitivement (photos comprises).
        </p>
      )}

      {data && data.total === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="colis-vide">
          <Package className="mx-auto mb-2 opacity-60" />
          Aucun colis pour l'instant. Cliquez sur « Scanner un colis ».
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table data-testid="colis-liste">
            <TableHeader>
              <TableRow>
                <TableHead>N° de colis</TableHead><TableHead>Expéditeur / destinataire</TableHead><TableHead>Type</TableHead><TableHead>Statut</TableHead><TableHead>Échéance</TableHead>
                <TableHead>Photos</TableHead><TableHead>{anciens && !q.trim() ? "Dernière info / suppression" : "Créé le"}</TableHead><TableHead>Par</TableHead><TableHead>Saisie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {liste.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/colis/${c.id}`)} data-testid={`colis-row-${c.numero}`}>
                  <TableCell className="font-mono font-bold">
                    {numeroLisible(c.numero)}
                    {c.depot && <span className="block font-sans font-normal text-[11px] text-muted-foreground">Dépôt : {c.depot.emplacement}</span>}
                    {c.cage && <span className="block font-sans font-normal text-[11px] text-muted-foreground">Cage : {c.cage.emplacement}</span>}
                  </TableCell>
                  <TableCell className="min-w-[200px]">{c.info ? <InfoFichier info={c.info} compact recherche={q} numero={c.numero} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{c.type_label || "—"}</TableCell>
                  <TableCell><StatutColis c={c} detail /></TableCell>
                  <TableCell>{c.echeance ? <EcheanceBadge e={c.echeance} testId={`echeance-${c.numero}`} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.photos?.length || "—"}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {anciens && !q.trim() ? (
                      <>{fmtDateHeure(c.derniere_activite)}<span className="block text-[11px] text-red-600 dark:text-red-400" data-testid={`suppression-${c.numero}`}>supprimée le {fmtDateHeure(c.suppression_le).split(" ")[0]}</span></>
                    ) : fmtDateHeure(c.created_at)}
                    {c.anterieur && q.trim() && <span className="block text-[11px] text-muted-foreground">antérieur</span>}
                  </TableCell>
                  <TableCell className="text-sm">{c.created_by?.nom || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.source === "scan" ? <span className="inline-flex items-center gap-1"><ScanLine size={12} /> Scan</span> : "Manuelle"}
                  </TableCell>
                </TableRow>
              ))}
              {data && !liste.length && <TableRow><TableCell colSpan={9} className="text-sm text-muted-foreground">{q.trim() ? "Aucune fiche colis ne correspond." : filtre ? "Aucun colis dans ce filtre." : "Aucun colis."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      <CreerListeDialog open={creerListe} onClose={() => setCreerListe(false)} />
      <AjoutColisDialog open={!!ajout} startScan={ajout === "scan"} codeInitial={codeLu} onClose={() => { setAjout(false); setCodeLu(null); }} onCreated={() => { charger(q, filtre, anciens); chargerLieux(); }} />
    </AppShell>
  );
}
